<template>
    <div class="w-full" :aria-busy="busy">
        <label v-if="label" class="vs-select--label">{{ label }}</label>

        <div class="flex flex-row items-center gap-2">
            <div class="flex-1">
                <Combobox
                    :value="value"
                    :options="options"
                    :label="null"
                    :multiple="multiple"
                    :placeholder="placeholder"
                    :cleareable="cleareable"
                    :error="error"
                    :errorMessage="errorMessage"
                    :disabled="disabled || busy"
                    @input="onSelect"
                />
            </div>

            <div class="flex flex-row items-center gap-2" :class="{ 'creatable-select__actions--busy': busy }">
                <feather-icon
                    icon="PlusIcon"
                    svgClasses="text-primary w-5"
                    class="cursor-pointer"
                    @click="openCreate"
                />
                <feather-icon
                    icon="RefreshCwIcon"
                    svgClasses="text-primary w-5"
                    class="cursor-pointer"
                    :class="{ 'creatable-select__spin': busy }"
                    @click="manualReload"
                />
            </div>
        </div>
    </div>
</template>

<script>

import { v4 as uuidv4 } from 'uuid'
import Combobox from "@/modules/Shared/Components/Combobox.vue"

// Delay before showing the refreshing feedback, so fast reloads don't flicker the spinner.
const REFRESH_SPINNER_DELAY = 150

export default {
    name: "CreatableSelect",
    components: { Combobox },
    props: {
        value: {
            type: [Array, String, Number],
            default: () => []
        },
        options: {
            type: Array,
            required: true,
        },
        label: {
            default: null
        },
        multiple: {
            type: Boolean,
            default: false
        },
        placeholder: {
            type: String,
            default: 'Select option'
        },
        cleareable: {
            type: Boolean,
            default: false
        },
        error: {
            type: Boolean,
            default: false
        },
        errorMessage: {
            type: String,
            default: "Select an option"
        },
        disabled: {
            type: Boolean,
            default: false
        },
        createRouteName: {
            type: String,
            required: true,
        },
        createRouteParamKey: {
            type: String,
            default: 'uuid'
        },
        reload: {
            type: Function,
            required: true,
        },
        resolveCreatedKey: {
            type: Function,
            required: true,
        },
        createdToOption: {
            type: Function,
            default: null,
        }
    },
    data() {
        return {
            pendingToken: null,
            busy: false,
            inFlight: 0,
            busyTimer: null,
        }
    },
    mounted() {
        window.addEventListener('message', this.onMessage)
        window.addEventListener('focus', this.onFocus)
        document.addEventListener('visibilitychange', this.onFocus)
    },
    beforeDestroy() {
        window.removeEventListener('message', this.onMessage)
        window.removeEventListener('focus', this.onFocus)
        document.removeEventListener('visibilitychange', this.onFocus)
        if (this.busyTimer) clearTimeout(this.busyTimer)
    },
    methods: {
        onSelect(value) {
            // user changed the selection by hand: drop any stale pending token
            this.pendingToken = null
            this.$emit('input', value)
        },
        openCreate() {
            this.pendingToken = uuidv4()
            const href = this.$router.resolve({
                name: this.createRouteName,
                params: { [this.createRouteParamKey]: this.pendingToken },
                query: { picker: 1 }
            }).href
            window.open(href, '_blank')
        },
        startBusy() {
            this.inFlight += 1
            if (this.inFlight === 1) {
                this.busyTimer = setTimeout(() => { this.busy = true }, REFRESH_SPINNER_DELAY)
            }
        },
        endBusy() {
            this.inFlight = Math.max(0, this.inFlight - 1)
            if (this.inFlight === 0) {
                if (this.busyTimer) {
                    clearTimeout(this.busyTimer)
                    this.busyTimer = null
                }
                this.busy = false
            }
        },
        async reloadWithFeedback() {
            this.startBusy()
            try {
                return await this.reload()
            } finally {
                this.endBusy()
            }
        },
        async manualReload() {
            this.$emit('reloaded', await this.reloadWithFeedback())
        },
        async handleCreated(payload) {
            const freshList = await this.reloadWithFeedback()
            let list = freshList
            const effective = payload || { uuid: this.pendingToken }
            let key = this.resolveCreatedKey(effective, list)

            if (key == null && this.createdToOption && payload) {
                const option = this.createdToOption(effective)
                list = [...freshList, option]
                key = option.key
            }

            this.$emit('reloaded', list)

            if (key == null) return // not visible yet: keep pending, retry on next focus

            this.$emit('input', key)
            this.$emit('created', payload)
            this.pendingToken = null
        },
        onMessage(event) {
            if (event.origin !== window.location.origin) return
            const data = event.data
            if (!data || data.source !== 'creatable-select') return
            if (!this.pendingToken || !data.payload || data.payload.uuid !== this.pendingToken) return
            this.handleCreated(data.payload)
        },
        onFocus() {
            if (document.visibilityState !== 'visible' || !this.pendingToken) return
            this.handleCreated(null)
        }
    }
}
</script>

<style scoped>
    @keyframes creatable-select-spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
    }

    .creatable-select__spin {
        animation: creatable-select-spin 1s linear infinite;
    }

    .creatable-select__actions--busy {
        pointer-events: none;
        opacity: 0.5;
    }
</style>
